import type { EmailCampaignId } from 'types/campaign'
import type {
  ButtonElement,
  CampaignSection,
  ContentBlock,
  DividerElement,
  EmailElement,
  ImageElement,
  LayoutBlock,
  PlaylistElement,
  SectionBlock,
  TextElement,
} from 'types/content-editor'
import type { Thunk } from 'types/redux'
import {
  campaignDetailsSave,
  campaignDetailsUpdate,
} from '@disco/state-management/actions/campaign-details-actions'
import {
  CAMPAIGN_EDITOR_DELETE_SECTION,
  CAMPAIGN_EDITOR_DUPLICATE_SECTION,
  CAMPAIGN_EDITOR_EDIT_MODE_SWITCH,
  CAMPAIGN_EDITOR_INSERT_SECTION_BLOCK,
  CAMPAIGN_EDITOR_LOAD,
  CAMPAIGN_EDITOR_MOVE_SECTION,
  CAMPAIGN_EDITOR_PLAYLIST_BLOCK_CHANGE,
  CAMPAIGN_EDITOR_TEXT_BLOCK_CHANGE,
  CAMPAIGN_EDITOR_UPDATE_GLOBAL_PROPS,
  CAMPAIGN_EDITOR_UPDATE_SECTION,
} from '@disco/constants/ActionTypes'
import {
  getCampaign,
  getCampaignFooters,
} from '@disco/state-management/selectors/campaigns'
import { DEFAULT_CAMPAIGN_TEMPLATE_ID } from '@disco/content-editor/default'
import {
  getButtonBlock,
  getColumnBlock,
  getDividerBlock,
  getImageAndTextBlock,
  getImageBlock,
  getPlaylistBlock,
  getSectionBlock,
  getTextBlock,
} from '@disco/content-editor/content-blocks'
import { regularMarkup } from '@disco/content-editor/template-utils'
/**
 * Sets a default template for a campaign if no template is currently set.
 * @param campaignId - ID of the campaign to set template for
 * @returns Thunk that sets default template if unset
 */
export const setTemplateIfUnset = (campaignId: EmailCampaignId): Thunk => {
  return (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })

    if (!campaign.template) {
      dispatch(
        campaignDetailsUpdate(campaignId, {
          template: DEFAULT_CAMPAIGN_TEMPLATE_ID,
        }),
      )
    }
  }
}
/**
 * Saves template content for a campaign if not already saved.
 * @param campaignId - ID of the campaign to save template content for
 * @returns Thunk that marks template content as saved
 */
export const saveTemplateContent = (campaignId: EmailCampaignId): Thunk => {
  return (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId,
    })

    if (
      campaign.template_data &&
      !campaign.template_data.template_content_saved
    ) {
      dispatch(
        campaignDetailsUpdate(campaignId, {
          ...campaign,
          template_data: {
            ...campaign.template_data,
            template_content_saved: true,
          },
        }),
      )
    }
  }
}
const _autosavingTimers = {}
const autosaveInterval = 60 * 1000 // min

const resetTimer = (dispatch, campaignId: EmailCampaignId) => {
  clearTimeout(_autosavingTimers[campaignId])
  _autosavingTimers[campaignId] = setTimeout(
    () => dispatch(autosaveIfItsTime(campaignId, false)),
    autosaveInterval,
  )
}

/**
 * Clears the autosave timer for a specific campaign.
 * @param campaignId - ID of the campaign to clear autosave timer for
 */
export const clearAutosaveTimer = (campaignId: EmailCampaignId) => {
  if (_autosavingTimers[campaignId]) {
    clearTimeout(_autosavingTimers[campaignId])
  }
}
/**
 * Triggers autosave for a campaign if enough time has passed or forced.
 * @param campaignId - ID of the campaign to autosave
 * @param flush - Whether to force save regardless of timing
 * @returns Thunk that conditionally autosaves the campaign
 */
export const autosaveIfItsTime = (
  campaignId: EmailCampaignId,
  flush: boolean,
): Thunk => {
  // autosave = throttle + wait + leading + trailing
  return async (dispatch, getState) => {
    const campaign = getCampaign(getState(), {
      campaignId: campaignId,
    })
    if (campaign.status === 'sent' || !campaign.dirty) return
    const savedRecently =
      campaign.saving ||
      new Date().getTime() - (campaign.savedAt?.getTime() || 0) <=
        autosaveInterval

    if (savedRecently && !flush) {
      resetTimer(dispatch, campaignId)
      return
    }

    // ensure that we trigger autosave only when footers loaded so there will be no surprises
    const { loaded: footersLoaded } = getCampaignFooters(getState())
    if (footersLoaded)
      await dispatch(campaignDetailsSave(campaignId, true, false))
  }
}
/**
 * Dispatches an action and triggers autosave for the campaign.
 * @param action - Action object to dispatch before autosaving
 * @returns Thunk that dispatches action and triggers autosave
 */
export const autosave = (action: Record<string, any>): Thunk => {
  return async (dispatch, getState) => {
    let flush = false

    if (action.section && action.section.editMode === false) {
      flush = true
    }

    const campaignId =
      action.campaignId || getState().emailCampaigns.campaignEditor.id
    dispatch(action)

    if (campaignId) {
      dispatch({
        type: 'CAMPAIGN_DETAILS_MARK_DIRTY',
        campaignId,
      })
      dispatch(autosaveIfItsTime(campaignId, flush))
    }
  }
}
/**
 * Duplicates a section in the campaign editor.
 * @param sectionIndex - Index of the section to duplicate
 * @returns Thunk that duplicates the section with autosave
 */
export const duplicateSection = (sectionIndex: number): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_DUPLICATE_SECTION,
    sectionIndex,
  })
/**
 * Deletes a section from the campaign editor.
 * @param sectionIndex - Index of the section to delete
 * @returns Thunk that deletes the section with autosave
 */
export const deleteSection = (sectionIndex: number): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_DELETE_SECTION,
    sectionIndex,
  })

const getBlocks = (blockType: ContentBlock): EmailElement[] => {
  const blocks: (
    | TextElement
    | ImageElement
    | PlaylistElement
    | ButtonElement
    | DividerElement
  )[] = []

  if (blockType === 'text') {
    blocks.push(getTextBlock(regularMarkup()))
  } else if (blockType === 'image') {
    blocks.push(getImageBlock(''))
  } else if (blockType === 'image-and-text') {
    blocks.push(...getImageAndTextBlock('', regularMarkup()))
  } else if (blockType === 'tracklist') {
    blocks.push(getPlaylistBlock())
  } else if (blockType === 'button') {
    blocks.push(
      getButtonBlock('New button', 'https://disco.ac', '#0693e3', '#fff'),
    )
  } else if (blockType === 'divider') {
    blocks.push(getDividerBlock('2px', '#000', 'solid', '10px 0px'))
  } else {
    throw new Error(`Unknown block type: ${blockType}`)
  }

  return blocks
}

const getLayoutBlock = (blockType: LayoutBlock) => {
  if (blockType === 'two-column-text') {
    return getSectionBlock([
      getColumnBlock(getBlocks('text')),
      getColumnBlock(getBlocks('text')),
    ])
  }

  if (blockType === 'two-column-text-image') {
    return getSectionBlock([
      getColumnBlock(getBlocks('text')),
      getColumnBlock(getBlocks('image')),
    ])
  }

  if (blockType === 'two-column-image-text') {
    return getSectionBlock([
      getColumnBlock(getBlocks('image')),
      getColumnBlock(getBlocks('text')),
    ])
  }

  if (blockType === 'two-column-image') {
    return getSectionBlock([
      getColumnBlock(getBlocks('image')),
      getColumnBlock(getBlocks('image')),
    ])
  }

  if (blockType === 'three-column-image') {
    return getSectionBlock([
      getColumnBlock(getBlocks('image')),
      getColumnBlock(getBlocks('image')),
      getColumnBlock(getBlocks('image')),
    ])
  }

  if (blockType === 'three-column-text') {
    return getSectionBlock([
      getColumnBlock(getBlocks('text')),
      getColumnBlock(getBlocks('text')),
      getColumnBlock(getBlocks('text')),
    ])
  }

  if (blockType === 'two-column-image-and-text') {
    return getSectionBlock([
      getColumnBlock(getBlocks('image-and-text')),
      getColumnBlock(getBlocks('image-and-text')),
    ])
  }

  if (blockType === 'three-column-image-and-text') {
    return getSectionBlock([
      getColumnBlock(getBlocks('image-and-text')),
      getColumnBlock(getBlocks('image-and-text')),
      getColumnBlock(getBlocks('image-and-text')),
    ])
  }

  if (blockType === 'two-column-tracklist') {
    return getSectionBlock([
      getColumnBlock(getBlocks('tracklist')),
      getColumnBlock(getBlocks('tracklist')),
    ])
  }

  throw new Error(`Unknown layout block: ${blockType}`)
}

/**
 * Inserts a new section block at the specified index.
 * @param sectionIndex - Index where to insert the section
 * @param blockType - Type of block to insert
 * @returns Thunk that inserts the section block with autosave
 */
export const insertSectionBlock = (
  sectionIndex: number,
  blockType: SectionBlock,
): Thunk => {
  let section = {}

  if (
    blockType === 'image-and-text' ||
    blockType === 'text' ||
    blockType === 'button' ||
    blockType === 'tracklist' ||
    blockType === 'image' ||
    blockType === 'divider'
  ) {
    section = getSectionBlock([getColumnBlock(getBlocks(blockType))])
  } else {
    section = getLayoutBlock(blockType)
  }

  return autosave({
    type: CAMPAIGN_EDITOR_INSERT_SECTION_BLOCK,
    sectionIndex,
    section,
  })
}
/**
 * Moves a section block from one position to another.
 * @param sourceSectionIndex - Index of the section to move
 * @param targetSectionIndex - Index where to move the section
 * @returns Thunk that moves the section with autosave
 */
export const moveSectionBlock = (
  sourceSectionIndex: number,
  targetSectionIndex: number,
): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_MOVE_SECTION,
    sourceSectionIndex,
    targetSectionIndex,
  })
/**
 * Updates rich text content in a specific block.
 * @param sectionIndex - Index of the section containing the text
 * @param columnIndex - Index of the column containing the text
 * @param contentIndex - Index of the content block
 * @param text - Text object with formatting
 * @param html - HTML representation of the text
 * @returns Thunk that updates the text block with autosave
 */
export const changeRichText = (
  sectionIndex: number,
  columnIndex: number,
  contentIndex: number,
  text: Record<string, any> | null | undefined,
  html: string | null | undefined,
): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_TEXT_BLOCK_CHANGE,
    sectionIndex,
    columnIndex,
    contentIndex,
    text,
    html,
  })
/**
 * Updates the playlist in a playlist block.
 * @param sectionIndex - Index of the section containing the playlist
 * @param columnIndex - Index of the column containing the playlist
 * @param contentIndex - Index of the content block
 * @param playlistId - ID of the playlist to set
 * @returns Thunk that updates the playlist block with autosave
 */
export const changePlaylist = (
  sectionIndex: number,
  columnIndex: number,
  contentIndex: number,
  playlistId: number,
): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_PLAYLIST_BLOCK_CHANGE,
    sectionIndex,
    columnIndex,
    contentIndex,
    playlistId,
  })
/**
 * Switches a section to edit mode.
 * @param sectionIndex - Index of the section to switch to edit mode
 * @returns Action object to switch section to edit mode
 */
export const switchToEditMode = (
  sectionIndex: number,
): {
  sectionIndex: number
  type: string
} => {
  return {
    type: CAMPAIGN_EDITOR_EDIT_MODE_SWITCH,
    sectionIndex,
  }
}
/**
 * Loads sections into the campaign editor.
 * @param sections - Array of campaign sections to load
 * @returns Action object to load campaign editor with sections
 */
export const loadCampaignEditor = (
  sections: CampaignSection[],
): {
  sections: Array<CampaignSection>
  type: string
} => ({
  type: CAMPAIGN_EDITOR_LOAD,
  sections,
})
/**
 * Updates a specific section in the campaign editor.
 * @param sectionIndex - Index of the section to update
 * @param section - Updated section object
 * @returns Thunk that updates the section with autosave
 */
export const updateCampaignEditorSection = (
  sectionIndex: number,
  section: CampaignSection,
): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_UPDATE_SECTION,
    sectionIndex,
    section,
  })
/**
 * Updates global properties for the campaign editor.
 * @param props - Object containing global properties to update
 * @returns Thunk that updates global properties with autosave
 */
export const updateCampaignEditorGlobalProps = (
  props: Record<string, any>,
): Thunk =>
  autosave({
    type: CAMPAIGN_EDITOR_UPDATE_GLOBAL_PROPS,
    props,
  })
